import { Document, Packer, Paragraph, TextRun, AlignmentType, PageOrientation, BorderStyle, ImageRun } from 'docx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import type { CertificateTemplate, CertificateData } from '@/components/certificates';

async function fetchImageAsUint8Array(url: string): Promise<Uint8Array | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const buffer = await blob.arrayBuffer();
    return new Uint8Array(buffer);
  } catch {
    return null;
  }
}

export const useCertificateGenerator = () => {
  
  const generateCertificatePdf = async (
    template: CertificateTemplate,
    data: CertificateData
  ): Promise<void> => {
    const fontName = {
      serif: 'Georgia',
      sans: 'Arial',
      elegant: 'Georgia',
      modern: 'Calibri',
      handwritten: 'Comic Sans MS',
    }[template.fontFamily] || 'Arial';

    const displayCompanyName = template.companyName || data.companyName || '';
    const displayCertTitle = template.certificateTitle || 'Certificate of Completion';
    const displayIssuerName = template.issuerName || data.issuerName || '';
    const displayIssuerTitle = template.issuerTitle || '';

    // Fetch logo and signature images if available
    let logoImageData: Uint8Array | null = null;
    let signatureImageData: Uint8Array | null = null;

    if (template.logoUrl) {
      logoImageData = await fetchImageAsUint8Array(template.logoUrl);
    }
    if (template.signatureUrl) {
      signatureImageData = await fetchImageAsUint8Array(template.signatureUrl);
    }

    const getBorderColor = () => template.accentColor.replace('#', '');
    const getPrimaryColor = () => template.primaryColor.replace('#', '');
    const getAccentColor = () => template.accentColor.replace('#', '');

    const children: Paragraph[] = [];

    // Spacer
    children.push(new Paragraph({ spacing: { after: 400 } }));

    // Company Logo (if available)
    if (logoImageData) {
      children.push(
        new Paragraph({
          alignment: template.logoPosition === 'top-left' 
            ? AlignmentType.LEFT 
            : template.logoPosition === 'top-right' 
              ? AlignmentType.RIGHT 
              : AlignmentType.CENTER,
          spacing: { after: 100 },
          children: [
            new ImageRun({
              data: logoImageData,
              transformation: { width: 120, height: 60 },
              type: 'png',
            }),
          ],
        })
      );
    }

    // Company Name
    if (displayCompanyName) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: displayCompanyName,
              size: 22,
              color: getPrimaryColor(),
              font: fontName,
            }),
          ],
        })
      );
    }
    
    // Certificate Title
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
        children: [
          new TextRun({
            text: `★  ${displayCertTitle.toUpperCase()}  ★`,
            size: 24,
            color: getPrimaryColor(),
            font: fontName,
            allCaps: true,
          }),
        ],
      })
    );
    
    // Certification Name (if provided)
    if (data.certificationName) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
          children: [
            new TextRun({
              text: data.certificationName,
              size: 48,
              bold: true,
              color: getPrimaryColor(),
              font: fontName,
            }),
          ],
        })
      );
    }
    
    // Spacer
    children.push(new Paragraph({ spacing: { after: 200 } }));
    
    // "This is to certify that"
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
        children: [
          new TextRun({
            text: 'This is to certify that',
            size: 22,
            color: '666666',
            font: 'Arial',
            italics: true,
          }),
        ],
      })
    );
    
    // Recipient Name
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
        children: [
          new TextRun({
            text: data.recipientName,
            size: 56,
            bold: true,
            color: getPrimaryColor(),
            font: fontName,
          }),
        ],
      })
    );
    
    // Decorative line
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: '━━━━━━━━━━━━━━━━━━━━━━',
            size: 24,
            color: getAccentColor(),
          }),
        ],
      })
    );
    
    // "has successfully completed"
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
        children: [
          new TextRun({
            text: 'has successfully completed the learning pathway',
            size: 22,
            color: '666666',
            font: 'Arial',
          }),
        ],
      })
    );
    
    // Pathway Name
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: data.pathwayName,
            size: 40,
            bold: true,
            color: getPrimaryColor(),
            font: fontName,
          }),
        ],
      })
    );
    
    // Score (if provided)
    if (data.score) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: 'with a score of ',
              size: 22,
              color: '666666',
              font: 'Arial',
            }),
            new TextRun({
              text: `${data.score}%`,
              size: 28,
              bold: true,
              color: getAccentColor(),
              font: 'Arial',
            }),
          ],
        })
      );
    }

    // Accreditation Text
    if (template.accreditationText) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: template.accreditationText,
              size: 18,
              color: '888888',
              font: 'Arial',
              italics: true,
            }),
          ],
        })
      );
    }
    
    // Spacer
    children.push(new Paragraph({ spacing: { after: 300 } }));

    // Signature image
    if (signatureImageData) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.RIGHT,
          spacing: { after: 50 },
          children: [
            new ImageRun({
              data: signatureImageData,
              transformation: { width: 150, height: 50 },
              type: 'png',
            }),
          ],
        })
      );
    }
    
    // Footer with date and issuer
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 200 },
        children: [
          new TextRun({
            text: `Issued on ${format(data.completionDate, 'MMMM d, yyyy')}`,
            size: 20,
            color: getPrimaryColor(),
            font: 'Arial',
          }),
        ],
      })
    );

    // Issuer name
    if (displayIssuerName) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.RIGHT,
          spacing: { before: 50 },
          children: [
            new TextRun({
              text: displayIssuerName,
              size: 20,
              color: getPrimaryColor(),
              font: fontName,
              bold: true,
            }),
          ],
        })
      );
    }

    // Issuer title
    if (displayIssuerTitle) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.RIGHT,
          spacing: { before: 0 },
          children: [
            new TextRun({
              text: displayIssuerTitle,
              size: 18,
              color: '666666',
              font: 'Arial',
            }),
          ],
        })
      );
    }
    
    // Company name in footer if provided
    if (displayCompanyName) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 100 },
          children: [
            new TextRun({
              text: displayCompanyName,
              size: 20,
              color: '666666',
              font: 'Arial',
            }),
          ],
        })
      );
    }

    // Footer text
    if (template.footerText) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 100 },
          children: [
            new TextRun({
              text: template.footerText,
              size: 16,
              color: '999999',
              font: 'Arial',
            }),
          ],
        })
      );
    }

    // Create a landscape document
    const doc = new Document({
      sections: [{
        properties: {
          page: {
            size: {
              orientation: PageOrientation.LANDSCAPE,
              width: 15840,
              height: 12240,
            },
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 720,
            },
            borders: template.borderStyle !== 'none' ? {
              pageBorderTop: {
                style: template.borderStyle === 'ornate' || template.borderStyle === 'double' ? BorderStyle.DOUBLE : BorderStyle.SINGLE,
                size: template.borderStyle === 'ornate' ? 24 : template.borderStyle === 'double' ? 16 : 8,
                color: getBorderColor(),
              },
              pageBorderRight: {
                style: template.borderStyle === 'ornate' || template.borderStyle === 'double' ? BorderStyle.DOUBLE : BorderStyle.SINGLE,
                size: template.borderStyle === 'ornate' ? 24 : template.borderStyle === 'double' ? 16 : 8,
                color: getBorderColor(),
              },
              pageBorderBottom: {
                style: template.borderStyle === 'ornate' || template.borderStyle === 'double' ? BorderStyle.DOUBLE : BorderStyle.SINGLE,
                size: template.borderStyle === 'ornate' ? 24 : template.borderStyle === 'double' ? 16 : 8,
                color: getBorderColor(),
              },
              pageBorderLeft: {
                style: template.borderStyle === 'ornate' || template.borderStyle === 'double' ? BorderStyle.DOUBLE : BorderStyle.SINGLE,
                size: template.borderStyle === 'ornate' ? 24 : template.borderStyle === 'double' ? 16 : 8,
                color: getBorderColor(),
              },
            } : undefined,
          },
        },
        children,
      }],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `Certificate_${data.recipientName.replace(/\s+/g, '_')}_${format(data.completionDate, 'yyyy-MM-dd')}.docx`;
    saveAs(blob, fileName);
  };

  return { generateCertificatePdf };
};
